import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  PersonOff as PersonOffIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import { toastApiError } from '../../../utils/errorMessage';
import {
  BranchManager,
  BranchManagerSearchParams,
  getBranchManagers,
  promoteEmployeeToManager,
  removeBranchManager,
  toggleManagerStatus
} from '../../../api/branch_api_data';
import { AllUsers, type EmployeeDto } from '../../../api/api_data';
import { getUserIdFromToken } from '../../../utils/jwt';
import { positionLabelTr, primaryRoleLabelTr } from '../../../utils/labels';

interface BranchManagersTabProps {
  branchId: string;
  branchName: string;
}

interface ManagerAssignmentModal {
  open: boolean;
  selectedUsers: string[];
  loading: boolean;
}

interface ConfirmationDialog {
  open: boolean;
  type: 'remove' | 'toggle';
  manager: BranchManager | null;
  title: string;
  message: string;
  loading: boolean;
}

// Loading skeleton component for stats cards
const StatsCardSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Skeleton variant="circular" width={56} height={56} />
        <Stack>
          <Skeleton variant="text" width={40} height={32} />
          <Skeleton variant="text" width={120} height={20} />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

const BranchManagersTab: React.FC<BranchManagersTabProps> = ({ branchId, branchName }) => {
  // State management - enhanced for Görev 1
  const [managers, setManagers] = useState<BranchManager[]>([]);
  const [stats, setStats] = useState({
    totalCount: 0,
    activeCount: 0,
    inactiveCount: 0
  });
  // Atanabilecek aday listesi artık ÇALIŞAN listesinden geliyor (mobil/web kayıtlı,
  // henüz MANAGER User'ı olmayan çalışanlar da atanabilsin diye).
  const [availableEmployees, setAvailableEmployees] = useState<EmployeeDto[]>([]);
  // BUG-4: "Yönetici Atama" modalı içi arama (ad/email).
  const [assignSearch, setAssignSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [lastLoadTime, setLastLoadTime] = useState<Date | null>(null);
  
  // Modals with enhanced loading states
  const [assignmentModal, setAssignmentModal] = useState<ManagerAssignmentModal>({
    open: false,
    selectedUsers: [],
    loading: false
  });
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialog>({
    open: false,
    type: 'remove',
    manager: null,
    title: '',
    message: '',
    loading: false
  });

  // Zaten bu şubenin aktif yöneticisi olan çalışanları (email ile eşleştirerek) aday
  // listesinden çıkar; ardından modal içi arama filtresini (ad/email) uygula.
  const activeManagerEmails = new Set(
    managers
      .filter((m) => m.isActive)
      .map((m) => (m.user.email || '').trim().toLowerCase())
      .filter(Boolean)
  );

  const filteredAvailableEmployees = availableEmployees
    .filter((e) => !activeManagerEmails.has((e.email || '').trim().toLowerCase()))
    .filter((e) => {
      const q = assignSearch.trim().toLowerCase();
      if (!q) return true;
      return `${e.firstName ?? ''} ${e.lastName ?? ''} ${e.email ?? ''} ${e.position ?? ''} ${e.branchName ?? ''}`
        .toLowerCase()
        .includes(q);
    });

  // GÖREV 1: Load managers data with enhanced error handling and performance tracking
  const loadManagers = useCallback(async (searchParams?: BranchManagerSearchParams) => {
    try {
      setLoading(true);
      setStatsLoading(true);
      
      const startTime = performance.now();
      
      // Use Promise.all for parallel API calls - Görev 1 requirement
      const response = await getBranchManagers(branchId, searchParams);
      
      const loadTime = performance.now() - startTime;
      
      // Update state with backend data - Görev 1 requirement
      setManagers(response.managers);
      setStats({
        totalCount: response.totalCount,
        activeCount: response.activeCount,
        inactiveCount: response.inactiveCount
      });
      
      setLastLoadTime(new Date());
      
      // Show success message only on manual refresh
      if (searchParams === undefined && managers.length > 0) {
        toast.success(`${response.managers.length} yönetici başarıyla yüklendi`);
      }
      
    } catch (error: any) {
      // Enhanced error handling - Görev 1 requirement
      toastApiError(error, 'Yöneticiler yüklenirken hata oluştu');
      
      // Set empty state on error
      setManagers([]);
      setStats({
        totalCount: 0,
        activeCount: 0,
        inactiveCount: 0
      });
    } finally {
      // Turn off loading states - Görev 1 requirement
      setLoading(false);
      setStatsLoading(false);
    }
  }, [branchId, managers.length]);

  // GÖREV 5: Search with debounce for performance and backend integration ready
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const performSearch = useCallback(() => {
    const searchParams: BranchManagerSearchParams = {};
    
    // GÖREV 5: Prepare search parameters for backend API
    if (searchTerm.trim()) {
      searchParams.name = searchTerm.trim();
    }
    
    if (statusFilter !== 'all') {
      searchParams.isActive = statusFilter === 'active';
    }
    
    // GÖREV 5: Call backend with search parameters (when Görev 4 is completed)
    const hasSearchParams = Object.keys(searchParams).length > 0;
    
    loadManagers(hasSearchParams ? searchParams : undefined);
  }, [searchTerm, statusFilter, loadManagers]);

  // GÖREV 5: Debounced search implementation - waits 300ms after user stops typing
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      performSearch();
    }, 300); // 300ms debounce for optimal UX
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm, statusFilter, performSearch, searchTimeout]); // ESLint fixed: added searchTimeout

  // Initial load on component mount - Görev 1 requirement
  useEffect(() => {
    loadManagers();
  }, [loadManagers]); // ESLint fixed: added loadManagers dependency

  // GÖREV 2: Load assignable employees for promotion - Enhanced with loading states.
  // Aday listesi çalışan listesinden gelir (mobil/web kayıtlı çalışanlar da dahil).
  const loadAvailableUsers = useCallback(async () => {
    try {
      setAssignmentModal(prev => ({ ...prev, loading: true }));

      const currentUserId = getUserIdFromToken();
      if (!currentUserId) {
        toast.error('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
        setAvailableEmployees([]);
        return;
      }

      // Diğer admin listeleri gibi geniş bir sayfa boyutu ile çalışanları çek.
      const response = await AllUsers(currentUserId, 0, 1000, 'firstName', 'asc');
      const employees: EmployeeDto[] = response.content || [];
      setAvailableEmployees(employees);

      if (employees.length === 0) {
        toast('Atanabilecek çalışan bulunamadı.', { icon: '' });
      }

    } catch (error: any) {
      toast.error(error?.message || 'Çalışanlar yüklenirken hata oluştu.');
      setAvailableEmployees([]);
    } finally {
      setAssignmentModal(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // Remove this useEffect as loadAvailableUsers should only be called when modal opens
  // useEffect(() => {
  //   loadAvailableUsers();
  // }, [loadAvailableUsers]);

  // Filter managers based on search and status - Client-side filtering until Görev 5 backend is ready
  const filteredManagers = managers.filter(manager => {
    const matchesSearch = searchTerm === '' || 
      (manager.user.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (manager.user.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (manager.user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && manager.isActive) ||
      (statusFilter === 'inactive' && !manager.isActive);

    return matchesSearch && matchesStatus;
  });

  // GÖREV 2: Handle assignment modal with enhanced UX
  const handleOpenAssignmentModal = async () => {
    setAssignSearch('');
    setAssignmentModal({ open: true, selectedUsers: [], loading: false });
    await loadAvailableUsers();
  };

  const handleCloseAssignmentModal = () => {
    setAssignSearch('');
    setAssignmentModal({ open: false, selectedUsers: [], loading: false });
  };

  const handleUserSelection = (userId: string) => {
    setAssignmentModal(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  // GÖREV 2: Seçili çalışanları "promote" endpoint'i ile şube yöneticisi yap.
  const handleAssignManagers = async () => {
    if (assignmentModal.selectedUsers.length === 0) {
      toast.error('Lütfen en az bir çalışan seçin.');
      return;
    }

    try {
      setAssignmentModal(prev => ({ ...prev, loading: true }));

      // Her seçili çalışan için promote endpoint'ini çağır.
      const results = await Promise.allSettled(
        assignmentModal.selectedUsers.map(employeeId =>
          promoteEmployeeToManager(branchId, employeeId)
        )
      );

      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failures = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      );

      if (successful > 0) {
        toast.success(`${successful} çalışan yönetici olarak atandı.`);
      }

      // Sessiz hata yerine backend mesajını göster.
      failures.forEach((failure) => {
        const message =
          failure.reason?.response?.data?.message ||
          failure.reason?.message ||
          'Yönetici atama sırasında hata oluştu.';
        toast.error(message);
      });

      // Başarılı atama varsa modalı kapat ve listeyi yenile.
      if (successful > 0) {
        handleCloseAssignmentModal();
        await loadManagers();
      }

    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Yönetici atama sırasında hata oluştu.');
    } finally {
      setAssignmentModal(prev => ({ ...prev, loading: false }));
    }
  };

  // GÖREV 3: Handle manager removal with enhanced confirmation and feedback
  const handleRemoveManager = (manager: BranchManager) => {
    setConfirmationDialog({
      open: true,
      type: 'remove',
      manager,
      title: 'Yönetici Kaldır',
      message: `${manager.user.firstName || 'İsim'} ${manager.user.lastName || 'Soyisim'} isimli yöneticiyi şubeden kaldırmak istediğinizden emin misiniz?`,
      loading: false
    });
  };

  // GÖREV 3: Handle manager status toggle with enhanced UX and immediate feedback
  const handleToggleManagerStatus = (manager: BranchManager) => {
    const newStatus = manager.isActive ? 'pasif' : 'aktif';
    
    setConfirmationDialog({
      open: true,
      type: 'toggle',
      manager,
      title: manager.isActive ? 'Yöneticiyi Pasif Yap' : 'Yöneticiyi Aktif Yap',
      message: `${manager.user.firstName || 'İsim'} ${manager.user.lastName || 'Soyisim'} isimli yöneticiyi ${newStatus} yapmak istediğinizden emin misiniz?`,
      loading: false
    });
  };

  // GÖREV 3: Enhanced confirmation action handler with better error handling and user feedback
  const handleConfirmAction = async () => {
    if (!confirmationDialog.manager) return;

    const manager = confirmationDialog.manager;
    const actionType = confirmationDialog.type;
    
    try {
      setConfirmationDialog(prev => ({ ...prev, loading: true }));
      
      if (actionType === 'remove') {
        // Backend expects userId, not managerId for removal - Görev 3 requirement
        await removeBranchManager(branchId, manager.user.id);
        toast.success(`${manager.user.firstName} ${manager.user.lastName} başarıyla kaldırıldı.`);
        
        // Synchronize React state
        setManagers(prev => prev.filter(m => m.user.id !== manager.user.id));
        setStats(prev => ({
          totalCount: prev.totalCount - 1,
          activeCount: prev.activeCount - (manager.isActive ? 1 : 0),
          inactiveCount: prev.inactiveCount - (manager.isActive ? 0 : 1)
        }));
      } else if (actionType === 'toggle') {
        // Use managerId for status toggle - Görev 3 requirement
        const updatedManager = await toggleManagerStatus(branchId, manager.id);

        // BUG FIX: Update local state with returned data from API
        // This ensures UI updates immediately without needing to refetch all managers
        setManagers(prevManagers =>
          prevManagers.map(m =>
            m.id === updatedManager.id ? updatedManager : m
          )
        );

        // Update stats to reflect the new active/inactive counts
        setStats(prev => ({
          ...prev,
          activeCount: prev.activeCount + (updatedManager.isActive ? 1 : -1),
          inactiveCount: prev.inactiveCount + (updatedManager.isActive ? -1 : 1)
        }));

        const newStatus = updatedManager.isActive ? 'aktif' : 'pasif';
        toast.success(`${manager.user.firstName} ${manager.user.lastName} başarıyla ${newStatus} yapıldı.`);
      }

      // Close dialog and refresh data - Görev 3 requirement
      setConfirmationDialog({ open: false, type: 'remove', manager: null, title: '', message: '', loading: false });

      // No longer need to refetch all managers - state is already updated with API response
      
    } catch (error: any) {
      // Provide specific error messages based on action type
      if (actionType === 'remove') {
        toast.error('Yönetici kaldırılırken hata oluştu. Lütfen tekrar deneyiniz.');
      } else {
        toast.error('Yönetici durumu güncellenirken hata oluştu. Lütfen tekrar deneyiniz.');
      }
    } finally {
      setConfirmationDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCancelAction = () => {
    setConfirmationDialog({ open: false, type: 'remove', manager: null, title: '', message: '', loading: false });
  };

  // Format date - updated for backend field name
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  if (loading || statsLoading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Yöneticiler yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
      <Stack spacing={3}>
        {/* Alert removed - using toast notifications now */}

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {branchName} - Yöneticiler
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenAssignmentModal}
            >
              Yönetici Ata
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadManagers()}
            >
              Yenile
            </Button>
          </Stack>
        </Stack>

        {/* Stats Cards - Görev 1: Backend verisi ile doldurulmuş istatistik kartları */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            {statsLoading ? (
              <StatsCardSkeleton />
            ) : (
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <PeopleIcon />
                    </Avatar>
                    <Stack>
                      <Typography variant="h4">
                        {stats.totalCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Toplam Yönetici
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {statsLoading ? (
              <StatsCardSkeleton />
            ) : (
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Stack>
                      <Typography variant="h4">
                        {stats.activeCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Aktif Yönetici
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {statsLoading ? (
              <StatsCardSkeleton />
            ) : (
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'error.main' }}>
                      <CancelIcon />
                    </Avatar>
                    <Stack>
                      <Typography variant="h4">
                        {stats.inactiveCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pasif Yönetici
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Filters */}
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Yönetici ara... (ad, soyad, email)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText={searchTerm ? `"${searchTerm}" için aranıyor...` : 'Ad, soyad veya email ile arama yapabilirsiniz'}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Durum"
                    onChange={(e) => {
                      setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                    }}
                  >
                    <MenuItem value="all">Tümü</MenuItem>
                    <MenuItem value="active">Aktif</MenuItem>
                    <MenuItem value="inactive">Pasif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {filteredManagers.length} yönetici listeleniyor
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Managers Table */}
        <Card>
          <CardHeader 
            title="Yöneticiler Listesi"
            action={
              <Chip 
                label={`${filteredManagers.length} Yönetici`}
                color="primary"
                variant="outlined"
              />
            }
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Yönetici</TableCell>
                  <TableCell>İletişim</TableCell>
                  <TableCell>Atanma Tarihi</TableCell>
                  <TableCell>Atayan</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredManagers.map((manager) => (
                  <TableRow key={manager.id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar 
                          src={manager.user.profilePictureUrl} 
                          sx={{ width: 40, height: 40 }}
                        >
                          {manager.user.firstName?.[0] || '?'}{manager.user.lastName?.[0] || '?'}
                        </Avatar>
                        <Stack>
                          <Typography variant="subtitle2">
                            {manager.user.firstName || 'İsim'} {manager.user.lastName || 'Soyisim'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {primaryRoleLabelTr(manager.user.roles)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {manager.user.email}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {manager.user.phoneNumber}
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(manager.assignedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {manager.assignedByUser ? 
                          `${manager.assignedByUser.firstName || 'İsim'} ${manager.assignedByUser.lastName || 'Soyisim'}` : 
                          'Sistem'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                          checked={manager.isActive}
                          onChange={() => handleToggleManagerStatus(manager)}
                          size="small"
                        />
                        <Chip
                          label={manager.isActive ? 'Aktif' : 'Pasif'}
                          color={manager.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Yöneticiyi Kaldır">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveManager(manager)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredManagers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
                        <PersonOffIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography variant="h6" color="text.secondary">
                          Yönetici bulunamadı
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Arama kriterlerinize uygun yönetici bulunmamaktadır.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Manager Assignment Modal */}
        <Dialog 
          open={assignmentModal.open} 
          onClose={handleCloseAssignmentModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Yönetici Atama
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Yönetici yapmak istediğiniz çalışanları seçin:
              </Typography>

              <TextField
                size="small"
                fullWidth
                placeholder="Çalışan ara... (ad, soyad, email)"
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
              />

              {assignmentModal.loading ? (
                <Stack spacing={2}>
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                </Stack>
              ) : (
                filteredAvailableEmployees.length === 0 ? (
                  <Alert severity="info">
                    {availableEmployees.length === 0
                      ? 'Atanabilecek çalışan bulunmuyor.'
                      : 'Aramayla eşleşen çalışan yok.'}
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {filteredAvailableEmployees.map((employee) => (
                      <Paper
                        key={employee.id}
                        variant="outlined"
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          bgcolor: assignmentModal.selectedUsers.includes(employee.id) ? 'action.selected' : 'background.paper'
                        }}
                        onClick={() => handleUserSelection(employee.id)}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar>
                            {employee.firstName?.[0] || '?'}{employee.lastName?.[0] || '?'}
                          </Avatar>
                          <Stack sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2">
                              {employee.firstName || 'İsim'} {employee.lastName || 'Soyisim'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee.email}
                            </Typography>
                          </Stack>
                          <Stack alignItems="flex-end">
                            <Typography variant="body2" color="primary">
                              {positionLabelTr(employee.position) || 'Pozisyon yok'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee.branchName}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )
              )}
              
              {assignmentModal.selectedUsers.length > 0 && (
                <Alert severity="info">
                  {assignmentModal.selectedUsers.length} çalışan seçildi.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignmentModal}>
              İptal
            </Button>
            <Button 
              variant="contained" 
              onClick={handleAssignManagers}
              disabled={assignmentModal.selectedUsers.length === 0 || assignmentModal.loading}
            >
              Yönetici Ata ({assignmentModal.selectedUsers.length})
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog 
          open={confirmationDialog.open} 
          onClose={handleCancelAction}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {confirmationDialog.title}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {confirmationDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelAction}>
              İptal
            </Button>
            <Button 
              variant="contained" 
              color={confirmationDialog.type === 'remove' ? 'error' : 'primary'}
              onClick={handleConfirmAction}
              disabled={confirmationDialog.loading}
            >
              {confirmationDialog.type === 'remove' ? 'Kaldır' : 'Onayla'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default BranchManagersTab;
